import { search, normalize } from 'ss-search'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
import clone from 'lodash/clone'
import debounce from 'lodash/debounce'
import axios from 'axios'

// ajax cancelation token
const CancelToken = axios.CancelToken
let cancelationToken;

export default {
  emits: ['update:modelValue', 'update:searchValue'],
  props: {
    // selected items
    modelValue: {
      required: true
    },
    // list of available options
    options: {
      type: Array,
      required: true
    },
    // is select multiple?
    multiple: {
      type: Boolean,
      default: false
    },
    // default select placeholder
    defaultPlaceholder: {
      type: String,
      default: 'Vyberte'
    },
    // max options count to be showed to user
    maxOptions: {
      type: Number,
      default: 100
    },
    // custom handler for filtering options
    propFilteredOptions: {
      type: Function,
      default: null
    },
    // how many options can be selected?
    maxItems: {
      type: Number,
      default: null
    },
    // load items over ajax?
    ajaxUrl: {
      type: String,
      default: null,
    },
    // query param to used for search
    ajaxUrlQueryParam: {
      type: String,
      default: 'q'
    },
    // number of result before running first ajax call
    ajaxFirstCount: {
      type: Number,
      default: null,
    },
    // transform function(label) which returns object { count: <totalFoundResult>, results: [ {id, label} ] }
    ajaxTransformResults: {
      type: Function,
      default: (response) => {
        return response.data
      },
    },
    // new option handler which returns object for new item {id, label}
    newOptionHandler: {
      type: Function,
      default: null,
    },
    lockedItems: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  data() {
    let value = []
    let selectedOptions = []
    
    // prepare values
    if (this.modelValue !== null) {
      value = this.multiple === false
        ? [clone(this.modelValue)]
        : clone(this.modelValue)
      
      selectedOptions = value.map(itemId => find(this.options, ['id', itemId]))
    }

    let placeholder = this.defaultPlaceholder
    
    if (this.multiple === false && selectedOptions.length === 1) {
      placeholder = selectedOptions[0].label
    
    } else if (this.multiple && selectedOptions.length > 0) {
      placeholder = ''
    }

    return {
      cursor: null,
      searchInput: {
        placeholder,
        value: ''
      },
      opened: false,
      value,
      selectedOptions,
      searchInputEl: null,
      selectedOptionEl: null,
      optionsContainerEl: null,
      keyUsed: false,
      keyUsedTimeout: null,
      // ajax
      ajaxDebounce: null,
      ajaxResultsCount: this.ajaxFirstCount,
      ajaxResults: null,
      loading: false,
      // new option loading
      interactedAddOption: false,
    }
  },
  computed: {
    optionsStage1() { // use ajax loaded options or given prop options?
      return this.ajaxResults
        ? this.ajaxResults
        : this.options
    },
    optionsStage2() { // filter options by search
      if (this.propFilteredOptions) {
        return this.propFilteredOptions(this, this.optionsStage1)
      }

      if (this.searchInput.value == '') {
        return this.optionsStage1
      }
      
      return search(this.optionsStage1, ['label'], this.searchInput.value)
    },
    optionsStage3() { // prepend add new option?
      if (this.newOptionHandler === null || this.searchInput.value == '') {
        return this.optionsStage2
      }
      
      const normalizedSearch = normalize(this.searchInput.value)
      const addNewOption = this.selectedOptions.filter((option) => normalizedSearch === normalize(option.label)).length === 0
        && this.optionsStage2.filter((option) => normalizedSearch === normalize(option.label)).length === 0

      return addNewOption
        ? [{ id: normalizedSearch, label: this.searchInput.value, newOption: true }].concat(this.optionsStage2)
        : this.optionsStage2
    },
    showNoResults() {
      return this.filteredOptions.length === 0
    },
    showMaxResultsExceeded() {
      return this.filteredOptions.length > this.maxOptions
        || (this.ajaxResultsCount !== null && this.filteredOptions.length > 0 && this.optionsStage2.length < this.ajaxResultsCount)
    },
    resultsCount() {
      return this.ajaxResultsCount !== null
        ? this.ajaxResultsCount
        : this.filteredOptions.length
    },
    showMaxItemsSelected() {
      return this.maxItems !== null && this.maxItems <= this.value.length
    },
    filteredAndLimitedOptions() {
      return this.filteredOptions.slice(0, this.maxOptions)
    }
  },
  methods: {
    addKeyHandler() {
      window.addEventListener('keydown', this.keyHandler)
    },
    removeKeyHandler() {
      window.removeEventListener('keydown', this.keyHandler)
    },
    keyHandler(e) {
      const key = e.which || e.keyCode
      if (key === 38 /*|| (e.shiftKey && key === 9)*/) { // 38 — up
        this.handleKeyUp(e)
      } else if (key === 40 /*|| key === 9*/) { //  40 — down // 9 — tab
        this.handleKeyDown(e)
      } else if (key === 13) { //  13 — enter
        this.handleEnter(e)
      } else if (key === 27) { // 27 — escape
        e.preventDefault()
        this.closeSelect()
      } else if (key === 8) { // 8 - backspace
        if (this.value.length && this.searchInput.value.length === 0) {
          e.preventDefault()
          this.removeOption(this.selectedOptions.slice(-1)[0])
        }
      }
    },
    handleEnter(e) {
      e.preventDefault()
      if (this.cursor !== null) {
        this.selectOption(this.filteredOptions[this.cursor])
      }
    },
    handleKeyUp(e) {
      e.preventDefault()
      this.keyUsed = true
      if (this.cursor === null) return
      if (this.cursor <= 0) {
        // If index is less than or equal to zero then set it to the last item index
        this.cursor = this.filteredOptions.length - 1
      } else if (
        this.cursor > 0 &&
        this.cursor <= this.filteredOptions.length - 1
      ) {
        // If index is larger than zero and smaller or equal to last index then decrement
        this.cursor--
      }
    },
    handleKeyDown(e) {
      e.preventDefault()
      this.keyUsed = true
      if (this.cursor === null) return
      // Check if index is below 0
      // This means that we did not start yet
      if (
        this.cursor < 0 ||
        this.cursor === this.filteredOptions.length - 1
      ) {
        // Set the index to the first item
        this.cursor = 0
      } else if (
        this.cursor >= 0 &&
        this.cursor < this.filteredOptions.length - 1
      ) {
        this.cursor++
      }
    },
    handleMouseEnter(option) {
      if (this.keyUsed === true) return
      this.cursor = findIndex(this.filteredOptions, ['id', option.id])
    },
    async selectOption(option) {
      if (this.newOptionHandler && option.newOption === true) {
        this.interactedAddOption = true
        option = await this.newOptionHandler(option.label)
        this.interactedAddOption = false
        if (!option) {
          return
        }
      }
      
      if (!this.multiple) {
        this.searchInput.placeholder = option.label
        this.searchInput.value = ''

        this.value = [option.id]
        this.selectedOptions = [option]
        this.$emit('update:modelValue', this.value[0])

        if (this.searchInputEl) {
          this.searchInputEl.blur()
        }
        this.opened = false
      } else if (this.value.indexOf(option.id) === -1) {
        if (this.showMaxItemsSelected) return
        this.searchInput.placeholder = ''
        this.value.push(option.id)
        this.selectedOptions.push(option)
        this.$emit('update:modelValue', this.value)
      } else {
        this.removeOption(option)
      }
    },
    removeOption(option) {
      if (this.lockedItems.includes(option.id)) {
        return
      }

      const idx2Delete = this.value.indexOf(option.id)
      this.value.splice(idx2Delete, 1)
      this.selectedOptions.splice(idx2Delete, 1)
      this.$emit('update:modelValue', this.value)
      if (this.value.length === 0) {
        this.searchInput.placeholder = this.defaultPlaceholder
      }
    },
    closeSelect() {
      this.searchInput.value = ''
      if (this.searchInputEl) {
        this.searchInputEl.blur()
      }
      this.opened = false
    },
    openSelect() {
      if (this.searchInputEl) {
        this.searchInputEl.focus()
      }
      this.opened = true
    },
    searchInputElRef(el) {
      this.searchInputEl = el
    },
    selectedOptionRef(el) {
      this.selectedOptionEl = el
    },
    optionsContainerRef(el) {
      this.optionsContainerEl = el
    },
    updateCursorIndex(useSelectedOption) {
      const filteredOptionsLength = this.filteredOptions.length
      if (filteredOptionsLength === 0 || (this.maxItems !== null && this.maxItems <= this.value.length)) {
        this.cursor = null
      } else if (useSelectedOption && this.filteredOptions.length && this.value.length === 1 && this.cursor === null) {
        let newCursor = findIndex(this.filteredOptions, ['id', this.value[0].id])

        if (newCursor !== -1) {
          this.cursor = newCursor
        } else {
          this.cursor = 0
        }
      } else if (this.cursor === null || this.cursor > filteredOptionsLength - 1) {
        this.cursor = 0
      }
    },
    // ajax loading
    async ajaxLoadOptions(question) {
      const url = this.ajaxUrl + '&' + this.ajaxUrlQueryParam + '=' + encodeURIComponent(question);

      try {
        if (cancelationToken) {
          cancelationToken.cancel();
          cancelationToken = null
        }

        cancelationToken = CancelToken.source()

        const { results, count } = this.ajaxTransformResults(await axios.get(url, {
          cancelToken: cancelationToken.token,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        }))

        this.ajaxResults = results
        this.ajaxResultsCount = count
      } catch (e) {
        // error
        console.error(e);
      }

      this.loading = false
    }
  },
  watch: {
    'searchInput.value'(val) {
      this.cursor = null
      this.$emit('update:searchValue', val)

      this.ajaxResults = null
      this.ajaxResultsCount = this.ajaxFirstCount

      if (!this.ajaxUrl) {
        return
      }

      if (this.ajaxDebounce !== null) {
        this.ajaxDebounce.cancel()
      }

      if (val == '') {
        return
      }

      this.ajaxDebounce = debounce(() => {
        this.loading = true
        this.ajaxLoadOptions(val)
      }, 500)

      this.ajaxDebounce()
    },
    modelValue(val) {
      if (val !== null) {
        this.value = this.multiple === false ? [clone(val)] : clone(val)
        this.selectedOptions = this.value.map(itemId => find(this.options, ['id', itemId]))
      
      } else {
        this.value = []
        this.selectedOptions = []
        this.searchInput.placeholder = this.defaultPlaceholder
        this.searchInput.value = ''
      }
    },
    filteredOptions() {
      this.updateCursorIndex(false)
    },
    cursor: {
      handler(val) { // scroll to if option element is not visible
        if (val === null || !this.selectedOptionEl || !this.optionsContainerEl || !this.keyUsed) {
          this.keyUsed = false
          return
        }

        if (this.keyUsedTimeout) clearTimeout(this.keyUsedTimeout)
        const { bottom, height, top } = this.selectedOptionEl.getBoundingClientRect()
        const containerRect = this.optionsContainerEl.getBoundingClientRect()

        // if element is visible in parent
        if (top <= containerRect.top ? containerRect.top - top < height : bottom - containerRect.bottom < height) {
          this.keyUsed = false
          return
        }

        this.selectedOptionEl.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest'
        })

        this.keyUsedTimeout = setTimeout(() => {
          this.keyUsed = false
        }, 1000)
      },
      flush: 'post'
    }
  }
}